import { useEffect } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Button, CircularProgress, Typography } from "@avenue-8/sidekick-ui";
import { keepPreviousData } from "@tanstack/react-query";
import { useListSuggestions } from "@shared/generated/crm";
import { TablePagination } from "@shared/components/TablePagination";
import { ProfileSuggestionCard } from "./ProfileSuggestionCard";
import { useProfileSuggestionReview } from "./useProfileSuggestionReview";

const PAGE_SIZE = 25;

type ProfileReviewListProps = {
  page: number;
  onChangePage: (page: number) => void;
};

export const ProfileReviewList = ({
  page,
  onChangePage,
}: ProfileReviewListProps) => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError, refetch } = useListSuggestions(
    { status: "pending", pageNumber: page, pageSize: PAGE_SIZE },
    undefined,
    { query: { placeholderData: keepPreviousData } },
  );
  const { hiddenSuggestionIds, reviewProfileSuggestion } =
    useProfileSuggestionReview();

  const suggestions = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);
  const visibleSuggestions = suggestions.filter(
    (suggestion) => !hiddenSuggestionIds.has(suggestion.id),
  );
  const remainingCount =
    (data?.total ?? 0) - (suggestions.length - visibleSuggestions.length);

  useEffect(() => {
    if (isLoading || isFetching) return;
    const lastPage = Math.max(1, totalPages);
    if (page > lastPage) onChangePage(lastPage);
  }, [isLoading, isFetching, page, totalPages, onChangePage]);

  if (isError) {
    return (
      <StateBox>
        <Typography>
          Something went wrong loading profiles to review.
        </Typography>
        <Button variant="outlined" size="small" onClick={() => refetch()}>
          Refresh
        </Button>
      </StateBox>
    );
  }

  if (isLoading) return <SuggestionSkeletons />;

  if (remainingCount === 0) {
    return (
      <StateBox>
        <EmptyIcon />
        <Typography variant="h6">You&apos;re all caught up</Typography>
        <Typography variant="body2">
          New possible profiles show up here as Radar finds them for your
          monitored contacts.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate("/contacts")}
        >
          Back to Contacts
        </Button>
      </StateBox>
    );
  }

  return (
    <>
      {visibleSuggestions.length === 0 ? (
        <StateBox role="status">
          <CircularProgress size={24} />
          <Typography>
            The next profiles will show up here in a few seconds.
          </Typography>
        </StateBox>
      ) : (
        <List>
          {visibleSuggestions.map((suggestion) => (
            <ProfileSuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              onAccept={() => reviewProfileSuggestion(suggestion, "accepted")}
              onReject={() => reviewProfileSuggestion(suggestion, "rejected")}
            />
          ))}
        </List>
      )}
      <TablePagination
        page={page}
        totalPages={totalPages}
        onChange={onChangePage}
      />
    </>
  );
};

const SuggestionSkeletons = () => (
  <List>
    {Array.from({ length: 3 }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={180} />
    ))}
  </List>
);

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  text-align: center;
`;

const EmptyIcon = styled(TaskAltIcon)`
  font-size: 2.5rem;
  color: ${(p) => p.theme.colors.success};
`;
